/**
 * The one entity-switch sequence (DV-3011, Phase A of DV-3007). All five
 * switch surfaces (four call sites) — org picker, accounts console, header tree,
 * deep-link guard, post-registration — call this instead of hand-rolling the steps.
 *
 * Sequence and every deliberate behavior decision are documented in
 * docs/specs/2026-08-31-dv3011-switch-entity-service-design.md (§3.2, §4).
 * UI-free by design: callers own spinners, toasts, and route-level error
 * surfaces. Plain function (not a hook) so Phase B can call it from
 * `bootstrapAuth` before React renders.
 */
import { LoginConstants } from '../config/keyClock.config';
import { scopeRootOfToken, tokenSubject } from './entity-claim';
import { toRealmRole } from './entity-resolution';
import { clearOrgScopedDrafts } from '../util/persistedState';
import {
  clearDeniedIntendedTarget,
  consumeIntendedUrl,
} from './intended-navigation';
import { landOn } from './navigation-port';
import { watermarkSignal } from './entity-doc';
import { announceScopeChange } from './scope-broadcast';
import { fetchEntityInfoRequest } from './entity-scope-api';
import { beginSwitch, endSwitch } from './switch-in-progress';
import {
  reanchor,
  reanchorVerdict,
  refreshIfRootless,
  type Verdict,
} from './entity-scope-reanchor';
import { reconcileScope } from './scope-reconciliation';
import { SessionEndingError, SwitchEntityError } from './switch-errors';
import { getResetAllApiState, getStore } from '../store/get-store';
import { scopeChanged } from '../slice/entityScopeSlice';
import type { EntityCheckoutInfo } from '../types/org-selection.types';

export { SwitchEntityError, type SwitchStage } from './switch-errors';

export interface SwitchEntityTarget {
  /** Entity to scope into (the leaf, for sub-entity switches). */
  entityId: string;
  /** Display/company name — becomes `selectedOrg` and the groupName prefix. */
  entityName: string;
  /** PascalCase role suffix (e.g. 'CustomerAdmin', 'InternalAdmin'). */
  role: string;
  /**
   * Top-level org the scope is anchored to — the entity-tree fetch key and
   * the Prepay & Save category anchor. Required so every caller states it:
   * a sub-entity switch inside the current tree passes the stored parent
   * (the anchor deliberately does not move); a top-level switch passes the
   * entity itself.
   */
  parentEntityId: string;
}

/**
 * WHERE a completed switch leaves the user. Not HOW it propagates: propagation
 * is now unconditional and identical for every mode — persist, refresh, drop
 * every api cache, announce the new scope (spec §3.4 / D5).
 *
 * The four old modes each carried its own propagation mechanism, three of them
 * a document load. Phase B's `'continue'` proved the cache reset was the real
 * mechanism on one call site; this makes it the only one.
 */
export type SwitchPropagation =
  /** Stay put. The mounted tree re-renders from the new scope — header tree. */
  | 'stay'
  /** Consume the stored intended URL and land there, else home — org pickers. */
  | 'land-intended'
  /** History-replacing landing on home — post-registration. */
  | 'land-home';

export async function switchEntity(
  target: SwitchEntityTarget,
  propagate: SwitchPropagation,
): Promise<void> {
  // `groupName` is both the `currentContext` value written below and, on the
  // re-anchor path, what user-service authorises the switch against.
  const groupName = `${target.entityName}${target.role}`;

  // DV-3573 moved authorisation from once-per-switch to once-per-request: the
  // scope guard refuses any `X-Entity-UUID` outside the caller's allowed set
  // on EVERY request, which the old `/users/context` check could not do —
  // it could not notice a selection that became invalid mid-session.
  // DV-3421 then restored the call for the case that per-request checking
  // cannot fix by itself: a target outside the set stays outside it forever
  // unless the token moves. See `reanchorVerdict` and `reanchor`.
  //
  // THIS REQUIRES `planocloud.entity-scope.guard.honour-selection=true`. With
  // it off the header is checked and never obeyed, so the UI would name the
  // new account while the backend still scoped by the token — the silent
  // wrong-account state DV-3006 exists to prevent.

  // Which side of the token's reach is the target on? A failure here is NOT
  // treated as "re-anchor to be safe" (review round 2, issue 5): guessing
  // cross-tree turns an ordinary same-tree switch into a Keycloak write, and
  // a refresh that then fails logs the user out of every tab. Not knowing is
  // a reason to stop, not to act — the caller surfaces it and the user
  // retries.
  //
  // The root is read BEFORE the verdict (review round 5, item 2): the verdict
  // is true of the token as it was then, and `commitSwitch` refuses to stamp a
  // scope under a root that has moved since.
  //
  // DV-3745: so a token that names NO account cannot decide a switch. A new
  // registrant's was minted before the registration wrote their account; the
  // scope check refuses it, the base query's retry refreshes it to the new
  // account mid-verdict, and the commit refuses the root that moved — on the
  // account chooser and the bootstrap's auto-select as much as right after
  // the registration. Adopt a fresh token first, so the verdict is decided
  // under the root it will be committed under (`refreshIfRootless`, best
  // effort: a failed refresh leaves the switch exactly as it was).
  await refreshIfRootless();
  const rootAtVerdict = scopeRootOfToken();
  let verdict: Verdict;
  try {
    verdict = await reanchorVerdict(target);
  } catch (cause) {
    throw new SwitchEntityError('scope-check', cause);
  }

  if (verdict === 'in-scope') {
    // DV-3008's contract: the token says what you MAY see, the header says
    // what you are looking AT. An in-tree switch costs no Keycloak write and
    // no token round trip — the DV-3573 win — because the claim already
    // permits the target. The entity-info read carries no selection (review
    // round 6, item C): this tab's current one may be exactly what the token
    // can no longer reach, which is how a user picking their own home company
    // used to fail here.
    let entityInfo: EntityCheckoutInfo[];
    try {
      entityInfo = await fetchEntityInfoRequest(target.entityId);
    } catch (cause) {
      throw new SwitchEntityError('entity-info', cause);
    }
    commitSwitch(target, groupName, entityInfo, rootAtVerdict);
  } else {
    // Read again rather than carried over from the verdict: the verdict
    // awaited, and a logout in another tab during it leaves nothing to act as.
    const userId = tokenSubject();
    const rootBefore = scopeRootOfToken();
    if (userId === null || rootBefore === null) {
      throw new SwitchEntityError('scope-check', new Error('no readable session to re-anchor'));
    }

    // Everything from here to the dispatch in `commitSwitch` runs inside the
    // window, and every exit — success, throw, or a throw from the commit
    // itself — leaves it (review round 4, issues 2 and 3). The handle means a
    // newer switch's window is never closed by this one.
    const handle = beginSwitch();
    let sessionEnding = false;
    try {
      // `reanchor` has just confirmed the token is still this user's and
      // still at `moved.root` (recovering, or failing correctly, when another
      // tab moved it — review of 0a17c730, item 1). Nothing awaits between
      // that and this synchronous commit, so its own root check holds.
      const moved = await reanchor(target, groupName, userId, rootBefore);
      commitSwitch(target, groupName, moved.entityInfo, moved.root);
    } catch (error) {
      sessionEnding = error instanceof SessionEndingError;
      throw error;
    } finally {
      // A reconcile trigger that arrived inside the window was stood down
      // ('switching'); replay it now, or a failed switch can sit on a moved
      // token with nothing left to notice (review round 6, item B). Not when
      // the session is ending: its own navigation is already pending.
      if (endSwitch(handle) && !sessionEnding) reconcileScope();
    }
  }

  // Tell the other tabs to look again (spec §3.6, trigger 1). After a
  // cross-tree switch they must: the shared token moved, and `reconcileScope`
  // reloads any tab whose selection it no longer reaches. After an in-tree
  // switch it is one more cheap cue to check whether the token changed hands.
  announceScopeChange();
  // The cached tree is dropped by `scopeChanged` — it belongs to the previous
  // parent, and a stale tree is worse than a missing one (DV-3011 D3).

  switch (propagate) {
    case 'stay':
      return;
    case 'land-intended': {
      const intended = consumeIntendedUrl();
      if (intended) landOn(intended.pathname, intended.queryParams);
      else landOn(LoginConstants.NAVIGATE_TO);
      return;
    }
    case 'land-home':
      landOn(LoginConstants.NAVIGATE_TO, undefined, { replace: true });
      return;
    default:
      return assertNever(propagate);
  }
}

/**
 * Make the switch the tab's scope. Synchronous on purpose — see the reset
 * below. Persisted only after the calls that can fail have succeeded (D7): the
 * stored scope is always a consistent snapshot, wholly old or wholly new.
 *
 * <p>`decidedUnder` is the token root the switch was decided under — read
 * before the verdict on the in-tree path, after the move on the re-anchor
 * path. If another tab has re-anchored the shared token during the awaits
 * since, the decision no longer holds, so the switch is abandoned rather than
 * stamped as valid under the new root (review round 5, item 2): stamping it
 * would make every check that compares against the stamp — the header path,
 * `reconcileScope`, the persisted-scope read — agree with a selection the
 * guard refuses, and nothing would ever notice. Abandoned, this tab keeps its
 * old stamp, which the reconciler sees has moved.
 */
function commitSwitch(
  target: SwitchEntityTarget,
  groupName: string,
  entityInfo: EntityCheckoutInfo[],
  decidedUnder: string | null,
): void {
  if (scopeRootOfToken() !== decidedUnder) {
    throw new SwitchEntityError(
      'scope-check',
      new Error('the token was re-anchored by another tab during the switch'),
    );
  }

  // These two writes keep localStorage in step for readers that have not moved
  // to the slice; the slice is the source of truth (see getCurrentContext /
  // getScopeRole). `processTokenData` deliberately does not re-derive either
  // value on a refresh — it would reset them to the login account (DV-3573
  // review finding 1) — so the re-anchor's refresh does not either.
  //
  // `role` moves with the account for the same reason: nothing re-derives it
  // any more, so a user who is CustomerAdmin in A and CustomerUser in B would
  // otherwise carry A's role into B.
  localStorage.setItem('currentContext', groupName);
  // Removed, not left behind, when the org row's role maps to nothing the UI
  // knows (user-service returns null for a customer group matching none of
  // its four): inheriting the PREVIOUS account's role would grant this one
  // whatever that role allowed. With nothing stored, every RBAC reader denies.
  const realmRole = toRealmRole(target.role);
  if (realmRole) localStorage.setItem('role', realmRole);
  else localStorage.removeItem('role');
  clearOrgScopedDrafts();
  // A completed switch changes which entities this session can reach, so a
  // deep link refused under the previous scope is no longer authoritatively
  // refused (DV-3012 Critical-1 marker). Cleared here because this is the one
  // sequence every switch surface funnels through.
  clearDeniedIntendedTarget();

  // Propagation is unconditional (D5).
  //
  // NOTHING MAY AWAIT BETWEEN THIS RESET AND THE DISPATCH BELOW. The reset
  // makes mounted RTK Query subscribers refetch; anything keyed off slice
  // scope (the entity tree's `parentId`, `useUnifiedSSE`'s `selectedEntity`)
  // would refetch against the OLD scope if a render interleaved. React 18's
  // batching and the absence of a yield point are what make the pair
  // effectively atomic today — an `await` inserted here would silently undo
  // that (PR #1150 review, finding 3).
  getResetAllApiState()();

  // Dispatched rather than written key-by-key (DV-3013, PR #1146 review
  // finding 1): the persistence middleware turns this into the scope-key
  // writes, so the in-memory source moves WITH the stored one.
  //
  // And dispatched HERE, after the reset. This is the instant every consumer
  // learns the scope moved, and with no document load left to hide it, each
  // one refetches immediately. Before the RESET — the spec's position (§3.4:
  // persist -> refresh -> scopeChanged -> reset) — the refetches go out, and
  // the reset then discards their answers with nothing left to re-trigger
  // them. That was the reason the header's entity tree came back empty after
  // a sub-entity switch. Pinned by the ordering test in entity-scope.test.ts.
  getStore().dispatch(
    scopeChanged({
      // DV-3573: the user this switch completes as. A switch never changes it;
      // only a different person logging in does, and that is the one case a tab
      // must not survive — see the slice's userChangedSince.
      subAtWrite: tokenSubject(),
      // DV-3421: the root the switch was decided under, checked above to still
      // be the token's — which is what other tabs compare against when the
      // shared token moves.
      rootAtWrite: decidedUnder,
      // The realm role, so readers need no conversion; null exactly when
      // `localStorage.role` was just removed above, so the two agree.
      role: realmRole,
      entityId: target.entityId,
      entityName: target.entityName,
      parentEntityId: target.parentEntityId,
      // Derived HERE rather than supplied by each caller (D7): this is the only
      // derivation that sees entity-service's hierarchy-resolved value
      // (DV-2981), and it comes off a response this switch already awaits — no
      // extra request. It also fixes tree-select's first-click `null` (BC-6),
      // which came from a tree-node lookup against state that had not been
      // populated yet.
      hasWatermark: watermarkSignal(entityInfo?.[0]),
      // Empty result: fail closed (require checkout) and keep going —
      // stranding the user here is strictly worse; see BC-5 / spec D5.
      skipCheckout: entityInfo?.[0]?.skipCheckout === true,
    }),
  );
}

/**
 * Exhaustiveness guard for `SwitchPropagation` (DV-3012 close-out §3, banked;
 * mandatory here because spec §3.4 changes the union's members). A mode added
 * without a case becomes a compile error rather than a silent no-op switch.
 */
const assertNever = (mode: never): never => {
  throw new Error(`switchEntity: unhandled propagation mode ${String(mode)}`);
};
